import { useCallback, useMemo, useState } from "react";
import { useSidebarPeek } from "./use-sidebar-peek";

/**
 * 상단 바(MainHeader) 토글로 여닫는 offcanvas 목차의 상태 한 벌 — hover 면 본문 위에 띄우고, 클릭하면 고정 펼침.
 * 토글은 상단 바에, 목차는 그 아래 다른 트리에 있어 상태를 페이지가 들고 양쪽에 나눠 준다:
 * - `headerToggle` → `<AppMainHeader sidebar={...} />`
 * - `open`·`setOpen` → `<SidebarProvider open onOpenChange />`, `peek` → `<DocumentSidebar peek />`
 * 페이지는 펼침이 바뀔 때만 다시 렌더된다 — hover 미리보기 열림은 DocumentSidebar 만 구독한다.
 * (icon 레일 목차는 패널이 곧 트리거라 DocumentSidebar 가 같은 미리보기 훅을 안에서 쓴다 — 이 훅은 필요 없다)
 */
export const useDocumentSidebarToggle = (defaultOpen = true) => {
  const [open, setOpen] = useState(defaultOpen);
  const peek = useSidebarPeek(!open);
  const { enter, leave } = peek;
  const onToggle = useCallback(() => setOpen((current) => !current), []);
  const onHoverChange = useCallback(
    (hovering: boolean) => (hovering ? enter() : leave()),
    [enter, leave],
  );

  return useMemo(
    () => ({
      open,
      setOpen,
      peek,
      headerToggle: { open, onToggle, onHoverChange },
    }),
    [open, peek, onToggle, onHoverChange],
  );
};

export type DocumentSidebarToggleState = ReturnType<
  typeof useDocumentSidebarToggle
>;
